// ── AGENT BUILDER ───────────────────────────────────────────────────────────
export async function buildAgent(agentConfig) {
  return request('/api/agent/build-agent', {
    method: 'POST',
    body: JSON.stringify(agentConfig),
  });
}
import { DEVICES, APPS, POLICIES, USERS } from '../data/mockData';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:4000';
const TIMEOUT  = 5000;

function getToken() { return localStorage.getItem('mdm_token'); }

export async function request(path, options = {}) {
  const controller = new AbortController();
  const tid = setTimeout(() => controller.abort(), TIMEOUT);
  const token = getToken();
  try {
    const res = await fetch(`${API_BASE}${path}`, {
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...(options.headers || {}),
      },
      signal: controller.signal,
      ...options,
    });
    clearTimeout(tid);
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      const err = new Error(body.message || `API ${res.status}`);
      err.status = res.status;
      err.body = body;
      throw err;
    }
    const text = await res.text();
    return text ? JSON.parse(text) : null;
  } catch (err) {
    clearTimeout(tid);
    throw err;
  }
}

async function withFallback(apiFn, mockData) {
  try { return await apiFn(); } catch { return mockData; }
}

// ── AUTH ──────────────────────────────────────────────────────────────────────
export async function loginRequest(email, password) {
  // API önce dene, hata alırsan demo login
  try {
    return await request('/auth/login', { method: 'POST', body: JSON.stringify({ email, password }) });
  } catch (err) {
    // Demo modunda sadece admin@corpmdm.com çalışır
    if (email === 'ofkoysu@gmail.com' || email === 'admin@corpmdm.com') {
      if (password === 'admin123') {
        return { token: 'mock-jwt-superadmin', user: { id:1, name:'Ömer Faruk KÖYSU', email, role:'Süper Admin', status:'approved' } };
      }
    }
    throw err;
  }
}

export async function registerRequest(body) {
  // Register her zaman API'ye gider (mock modda basit yanıt döner)
  try {
    return await request('/auth/register', { method: 'POST', body: JSON.stringify(body) });
  } catch (err) {
    if (err.status) throw err; // API'den gelen hata (409 conflict vb.)
    // Ağ hatası — mock
    return { message: 'Kayıt başarılı. Onay bekleniyor.', status: 'pending', user: { ...body, role: 'Okuyucu', status: 'pending' } };
  }
}

// ── DEVICES ───────────────────────────────────────────────────────────────────
export function fetchDevices() {
  return withFallback(() => request('/devices').then(d => d?.items ?? d), DEVICES);
}
export function fetchDeviceDetail(id) {
  return withFallback(() => request(`/devices/${id}`), DEVICES.find(d => d.id === Number(id)) || null);
}
export function createDevice(body) {
  return withFallback(() => request('/devices', { method:'POST', body:JSON.stringify(body) }), { ...body, id:Date.now(), status:'online', compliance:'compliant', lastSeen:'az önce' });
}
export function updateDevice(id, body) {
  return withFallback(() => request(`/devices/${id}`, { method:'PATCH', body:JSON.stringify(body) }), { ...DEVICES.find(d=>d.id===Number(id)), ...body });
}
export function sendCommand(deviceId, type, payload = {}) {
  return withFallback(() => request(`/devices/${deviceId}/commands`, { method:'POST', body:JSON.stringify({ type, payload }) }), { id:`cmd_${Date.now()}`, deviceId, type, status:'queued' });
}

// ── APPS ──────────────────────────────────────────────────────────────────────
export function fetchApps() { return withFallback(() => request('/apps'), APPS); }

// ── POLICIES ──────────────────────────────────────────────────────────────────
export function fetchPolicies() { return withFallback(() => request('/policies'), POLICIES); }

// ── USERS ─────────────────────────────────────────────────────────────────────
export function fetchUsers() { return withFallback(() => request('/users'), USERS); }

export function fetchPendingUsers() {
  return withFallback(() => request('/admin/users/pending'), { items: [], total: 0 });
}

export function createUser(body) {
  return withFallback(() => request('/users', { method:'POST', body:JSON.stringify(body) }), { ...body, id:Date.now(), devices:0, lastActive:'az önce', status:'approved' });
}

export function updateUser(id, body) {
  return withFallback(() => request(`/users/${id}`, { method:'PATCH', body:JSON.stringify(body) }), { ...USERS.find(u=>u.id===Number(id)), ...body });
}

export function deleteUser(id) {
  return withFallback(() => request(`/users/${id}`, { method:'DELETE' }), { success:true });
}

export async function approveUser(id, role = 'Okuyucu') {
  try {
    return await request(`/admin/users/${id}/approve`, { method:'POST', body: JSON.stringify({ role }) });
  } catch (err) {
    if (err.status) throw err;
    return { success: true }; // mock fallback
  }
}

export async function rejectUser(id, reason = '') {
  try {
    return await request(`/admin/users/${id}/reject`, { method:'POST', body:JSON.stringify({ reason }) });
  } catch (err) {
    if (err.status) throw err;
    return { success: true };
  }
}

// ── REPORTS ───────────────────────────────────────────────────────────────────
export function fetchReportsSummary() {
  return withFallback(() => request('/reports/summary'), { totalDevices:247, onlineDevices:198, compliantDevices:231, activeBreaches:6, weeklyEnrollments:32 });
}

// ── EKSİK ADMIN FONKSİYONLARI ────────────────────────────────────────────────
export function fetchAllUsersAdmin() {
  return request('/admin/users');
}

export function deleteUserAdmin(id) {
  return request(`/admin/users/${id}`, { method: 'DELETE' });
}

export function changeUserRole(id, role) {
  return request(`/admin/users/${id}/role`, { method: 'PATCH', body: JSON.stringify({ role }) });
}

// ── ENROLLMENT & MDM (QR Code) ───────────────────────────────────────────────
export async function createEnrollmentToken(data) {
  try {
    return await request('/api/enrollment/create-token', { method: 'POST', body: JSON.stringify(data) });
  } catch (err) {
    if (err.status) throw err;
    // Demo modda token'ı mock kullanıcı oluştur
    return {
      id: `token_${Date.now()}`,
      token: `${Date.now()}-demo-token`,
      qr_code: 'data:image/svg+xml;base64,...',
      device_type: data.device_type || 'iphone',
      expires_at: new Date(Date.now() + (data.expires_in_hours || 24) * 60 * 60 * 1000),
      message: 'Demo Mode - QR Kodu Oluşturuldu'
    };
  }
}

export async function enrollDevice(data) {
  try {
    return await request('/api/enrollment/enroll-device', { method: 'POST', body: JSON.stringify(data) });
  } catch (err) {
    if (err.status) throw err;
    // Demo fallback
    return {
      device_id: Date.now(),
      message: 'Cihaz kaydedildi (Demo Mode)',
      apps_assigned: 3
    };
  }
}

export async function fetchMdmApps(target_os = 'ios') {
  try {
    return await request(`/api/enrollment/mdm-apps?target_os=${target_os}`);
  } catch (err) {
    if (err.status) throw err;
    // Demo apps
    return {
      items: [
        { id: 1, name: 'Microsoft Outlook', bundle_id: 'com.microsoft.outlook', version: '4.25.0', is_mandatory: true, category: 'Verimlilik' },
        { id: 2, name: 'Microsoft Teams', bundle_id: 'com.microsoft.teams', version: '5.5.0', is_mandatory: true, category: 'İş Araçları' },
        { id: 3, name: 'Google Chrome', bundle_id: 'com.google.chrome.iphone', version: '119.0', is_mandatory: false, category: 'İnternet' },
      ],
      total: 3
    };
  }
}

export async function distributeApp(app_id, device_ids) {
  try {
    return await request('/api/enrollment/distribute-app', { 
      method: 'POST', 
      body: JSON.stringify({ app_id, device_ids }) 
    });
  } catch (err) {
    if (err.status) throw err;
    return {
      distributed: device_ids.length,
      total: device_ids.length,
      message: 'Uygulamalar dağıtıldı (Demo Mode)'
    };
  }
}

export async function fetchDeviceApps(device_id) {
  try {
    return await request(`/api/enrollment/device/${device_id}/apps`);
  } catch (err) {
    if (err.status) throw err;
    return {
      device_id,
      apps: [
        { id: 1, name: 'Microsoft Outlook', bundle_id: 'com.microsoft.outlook', status: 'installed' },
        { id: 2, name: 'Microsoft Teams', bundle_id: 'com.microsoft.teams', status: 'installing' }
      ],
      total: 2
    };
  }
}
